/**
 * TripProfileCard — surfaces the visitor's "captured once, reused
 * everywhere" trip identity at the top of the Trip view.
 *
 * Why this exists: a visitor planning multiple activities shouldn't
 * re-enter their name, email, and party size for every single one. The
 * checkout review step writes the values they typed into
 * `tripProfile` (server-backed via itinerary.metadata.trip_profile);
 * this card surfaces the captured values so the visitor can SEE that
 * they're stored, EDIT them in one place, and TRUST that subsequent
 * bookings will pre-fill from here.
 *
 * Two states:
 *   1. Empty (nothing captured yet)  → "Add your details" CTA pill
 *   2. Populated                      → summary line + Edit button
 *
 * Editing flips the card to an inline form with the same fields the
 * checkout review uses, so the visitor's mental model stays consistent.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useItinerary, type TripProfile } from '../context/ItineraryContext';

const TripProfileCard: React.FC = () => {
  const { t } = useTranslation();
  const { tripProfile, updateTripProfile } = useItinerary();
  const [editing, setEditing] = useState(false);

  const fullName = [
    tripProfile.contact?.name_first,
    tripProfile.contact?.name_last,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();
  const hasAnything =
    !!fullName ||
    !!tripProfile.contact?.email ||
    !!tripProfile.party ||
    !!tripProfile.default_date;

  if (editing) {
    return (
      <TripProfileEdit
        profile={tripProfile}
        onSave={p => {
          updateTripProfile(p);
          setEditing(false);
        }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <section className="wg-tripprofile">
      <div className="wg-tripprofile__head">
        <h3 className="wg-tripprofile__title">{t('trip_profile.title')}</h3>
        <button
          type="button"
          className={`wg-tripprofile__edit${hasAnything ? '' : ' wg-tripprofile__edit--cta'}`}
          onClick={() => setEditing(true)}
        >
          {hasAnything ? t('common.edit') : t('trip_profile.add')}
        </button>
      </div>
      {hasAnything ? (
        <ul className="wg-tripprofile__lines">
          {(fullName || tripProfile.contact?.email) && (
            <li className="wg-tripprofile__line">
              {fullName && <span>{fullName}</span>}
              {tripProfile.contact?.email && (
                <span className="wg-tripprofile__muted">
                  {tripProfile.contact.email}
                </span>
              )}
            </li>
          )}
          {tripProfile.party && (
            <li className="wg-tripprofile__line">
              <span>
                {t('trip_profile.party_summary', {
                  adults: tripProfile.party.adults,
                  children: tripProfile.party.children,
                })}
              </span>
            </li>
          )}
          {tripProfile.default_date && (
            <li className="wg-tripprofile__line">
              <span className="wg-tripprofile__muted">
                {t('trip_profile.default_date_prefix')}{' '}
                {new Date(
                  tripProfile.default_date + 'T12:00:00',
                ).toLocaleDateString(undefined, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            </li>
          )}
        </ul>
      ) : (
        <p className="wg-tripprofile__hint">{t('trip_profile.hint_empty')}</p>
      )}
    </section>
  );
};

const TripProfileEdit: React.FC<{
  profile: TripProfile;
  onSave: (p: Partial<TripProfile>) => void;
  onCancel: () => void;
}> = ({ profile, onSave, onCancel }) => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState(profile.contact?.name_first || '');
  const [lastName, setLastName] = useState(profile.contact?.name_last || '');
  const [email, setEmail] = useState(profile.contact?.email || '');
  const [adults, setAdults] = useState<number>(profile.party?.adults ?? 2);
  const [kids, setKids] = useState<number>(profile.party?.children ?? 0);
  const [defaultDate, setDefaultDate] = useState<string>(
    profile.default_date || '',
  );

  const submit = () => {
    onSave({
      contact: {
        name_first: firstName.trim() || undefined,
        name_last: lastName.trim() || undefined,
        email: email.trim() || undefined,
      },
      party: { adults, children: kids },
      default_date: defaultDate || undefined,
    });
  };

  return (
    <section className="wg-tripprofile wg-tripprofile--edit">
      <div className="wg-tripprofile__head">
        <h3 className="wg-tripprofile__title">
          {t('trip_profile.edit_title')}
        </h3>
      </div>
      <div className="wg-tripprofile__form">
        <div className="wg-tripprofile__row">
          <label className="wg-tripprofile__field">
            <span>{t('checkout.first_name_label')}</span>
            <input
              className="wg-checkout__input"
              type="text"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              autoComplete="given-name"
            />
          </label>
          <label className="wg-tripprofile__field">
            <span>{t('checkout.last_name_label')}</span>
            <input
              className="wg-checkout__input"
              type="text"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              autoComplete="family-name"
            />
          </label>
        </div>
        <label className="wg-tripprofile__field">
          <span>{t('checkout.email')}</span>
          <input
            className="wg-checkout__input"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            autoComplete="email"
          />
        </label>
        <div className="wg-tripprofile__row">
          <Stepper
            label={t('checkout.adults_label')}
            value={adults}
            min={0}
            max={20}
            onChange={setAdults}
          />
          <Stepper
            label={t('checkout.children_label')}
            value={kids}
            min={0}
            max={20}
            onChange={setKids}
          />
        </div>
        <label className="wg-tripprofile__field">
          <span>{t('trip_profile.default_date_label')}</span>
          <input
            className="wg-checkout__input"
            type="date"
            value={defaultDate}
            onChange={e => setDefaultDate(e.target.value)}
            min={new Date().toISOString().slice(0, 10)}
          />
        </label>
      </div>
      <div className="wg-tripprofile__actions">
        <button
          type="button"
          className="wg-checkout__btn wg-checkout__btn--secondary"
          onClick={onCancel}
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="wg-checkout__btn wg-checkout__btn--primary"
          onClick={submit}
        >
          {t('common.save')}
        </button>
      </div>
    </section>
  );
};

const Stepper: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}> = ({ label, value, min, max, onChange }) => (
  <div className="wg-stepper">
    <span className="wg-stepper__label">{label}</span>
    <div className="wg-stepper__row">
      <button
        type="button"
        className="wg-stepper__btn"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        aria-label="−"
      >
        −
      </button>
      <span className="wg-stepper__value">{value}</span>
      <button
        type="button"
        className="wg-stepper__btn"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        aria-label="+"
      >
        +
      </button>
    </div>
  </div>
);

export default TripProfileCard;
